'use client';

import { useEffect, useState, useTransition } from 'react';

import { compareExtendedSubscriptionLevel } from '@ps/features/subscription/configuration/functions/subscription-display';
import { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';
import { Button } from '@ps/ui/atoms/button';
import { Dialog, DialogDescription, DialogTitle } from '@ps/ui/atoms/dialog';
import { FormProvider, useForm } from 'react-hook-form';

import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import { updateChannel } from '../../functions/channel/updateChannel';
import type { ChannelSubscriptionLevel } from '../../types/ChannelSubscriptionLevel';
import type { CommunityChannel } from '../../types/CommunityChannel';
import { ChannelFormDialogContent } from '../common/ChannelFormDialogContent';
import {
  ChannelFormFields,
  type ChannelFormValues,
} from '../common/ChannelFormFields';
import {
  type ApplySubscriptionLevelChoice,
  ChangeChannelSubscriptionLevelRemind,
} from './ChangeChannelSubscriptionLevelRemind';

interface Props {
  channel: CommunityChannel;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

function toFormValues(channel: CommunityChannel): ChannelFormValues {
  return {
    name: channel.name,
    description: channel.description ?? '',
    icon: channel.icon,
    visibility: channel.visibility,
    isBroadcastChannel: channel.isBroadcastChannel,
    minimumSubscriptionLevel:
      channel.minimumSubscriptionLevel as unknown as PopStoreExtendedSubscriptionLevel,
  };
}

/**
 * Edit Channel modal — surface for changing a channel's name,
 * description, visibility, icon, broadcast toggle, and minimum
 * subscription level. Required fields are validated client-side
 * (react-hook-form) before the save fires. When the creator raises the
 * level, the `ChangeChannelSubscriptionLevelRemind` dialog asks whether to
 * grandfather existing members or apply the new requirement to
 * everyone before the save fires.
 *
 * On success, the Firestore channel-doc subscription delivers the
 * patched fields to the list and detail panes — no manual refresh
 * needed.
 */
export function EditChannelModal({ channel, open, onOpenChange }: Props) {
  const { creatorId, refreshChannels } = useCommunityContext();
  const form = useForm<ChannelFormValues>({
    defaultValues: toFormValues(channel),
  });
  const [grandfatherPromptOpen, setGrandfatherPromptOpen] = useState(false);
  const [apiError, setApiError] = useState<string | undefined>();
  const [pending, startTransition] = useTransition();

  const previousLevel =
    channel.minimumSubscriptionLevel as unknown as PopStoreExtendedSubscriptionLevel;

  // Reset draft when the modal (re)opens for a different channel.
  useEffect(() => {
    if (open) {
      form.reset(toFormValues(channel));
      setGrandfatherPromptOpen(false);
      setApiError(undefined);
    }
  }, [
    open,
    channel.id,
    channel.name,
    channel.description,
    channel.visibility,
    channel.icon,
    channel.isBroadcastChannel,
    channel.minimumSubscriptionLevel,
    form,
  ]);

  const saveChannel = (
    values: ChannelFormValues,
    applySubscriptionLevelToNewMembersOnly: boolean,
  ) => {
    if (!creatorId || pending) {
      return;
    }
    setApiError(undefined);
    startTransition(async () => {
      try {
        await updateChannel({
          creatorId,
          channelId: channel.id,
          name: values.name.trim(),
          description: values.description.trim(),
          visibility: values.visibility,
          icon: values.icon ?? '',
          minimumSubscriptionLevel:
            values.minimumSubscriptionLevel as unknown as ChannelSubscriptionLevel,
          isBroadcastChannel: values.isBroadcastChannel,
          applySubscriptionLevelToNewMembersOnly,
        });
        await refreshChannels();
        onOpenChange(false);
      } catch (error) {
        console.error('updateChannel failed:', error);
        setApiError('Couldn’t save your changes. Please try again.');
      }
    });
  };

  const onSubmit = form.handleSubmit(values => {
    const isRaisingLevel =
      compareExtendedSubscriptionLevel(
        values.minimumSubscriptionLevel,
        previousLevel,
      ) > 0;
    if (isRaisingLevel) {
      setGrandfatherPromptOpen(true);
      return;
    }
    saveChannel(values, true);
  });

  const onConfirmGrandfather = (choice: ApplySubscriptionLevelChoice) => {
    setGrandfatherPromptOpen(false);
    saveChannel(form.getValues(), choice === 'apply-to-new-members');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ChannelFormDialogContent>
        <DialogTitle className="text-content-1 text-center text-xl font-semibold">
          Edit Channel
        </DialogTitle>
        <DialogDescription className="text-content-2 text-center text-xs">
          Update the channel name, description, icon, or visibility.
        </DialogDescription>

        <FormProvider {...form}>
          <ChannelFormFields idPrefix="ec" disabled={pending} />
        </FormProvider>

        {apiError ? (
          <p
            role="alert"
            className="bg-danger/10 rounded-md border border-red-500/30 px-3 py-2 text-xs text-red-200"
          >
            {apiError}
          </p>
        ) : null}

        <Button
          type="button"
          onClick={onSubmit}
          disabled={pending}
          className="bg-brand hover:bg-brand-hover-hover text-content-on-brand mt-2 w-full font-semibold"
        >
          {pending ? 'Saving…' : 'Save Changes'}
        </Button>
      </ChannelFormDialogContent>
      <ChangeChannelSubscriptionLevelRemind
        open={grandfatherPromptOpen}
        onOpenChange={setGrandfatherPromptOpen}
        onConfirm={onConfirmGrandfather}
      />
    </Dialog>
  );
}
